import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getGetCollectionMembersQueryKey,
  getGetCollectionsForDepartmentQueryKey,
  getGetCollectionTicketClaimsQueryKey,
  getListCollectionTicketClaimMemberStatesQueryKey,
  useGetCollectionMembersHook,
  useGetCollectionsForDepartmentHook,
  useGetCollectionTicketClaimsHook,
  useGetCurrentDepartmentTree,
  useListCollectionTicketClaimMemberStatesHook,
} from '@/api/trafikportalen';
import type { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { studentsWithClaimErrors } from '@/helpers/studentClaimErrors';
import type { StudentClaimError } from '@/helpers/studentClaimErrors';
import { findDepartmentByCollectionType } from '@/hooks/useGetStudentCollection';
import { useAccessibleStudentCategories } from '@/hooks/useAccessibleStudentCategories';

export const useStudentClaimErrors = (): {
  errors: StudentClaimError[];
  isLoading: boolean;
} => {
  const categories = useAccessibleStudentCategories();
  const { data: departmentTree, isLoading: isTreeLoading } =
    useGetCurrentDepartmentTree({
      query: { enabled: categories.length > 0 },
    });

  const departments = useMemo(
    () =>
      categories.map((type) => ({
        type,
        departmentId: departmentTree
          ? (findDepartmentByCollectionType(departmentTree, type)?.id ?? null)
          : null,
      })),
    [categories, departmentTree],
  );

  const getCollections = useGetCollectionsForDepartmentHook();
  const collectionQueries = useQueries({
    queries: departments.map(({ type, departmentId }) => ({
      queryKey: departmentId
        ? getGetCollectionsForDepartmentQueryKey(departmentId)
        : ['student-claim-errors', 'collections', type],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getCollections(departmentId ?? '', signal),
      enabled: !!departmentId,
    })),
  });

  const collections = useMemo(
    () =>
      departments.map((department, index) => {
        const match = collectionQueries[index]?.data?.find(
          (collection) => collection.type === department.type,
        );
        return {
          type: department.type,
          collectionId: match?.id ?? null,
        };
      }),
    [departments, collectionQueries],
  );

  const getMembers = useGetCollectionMembersHook();
  const memberQueries = useQueries({
    queries: collections.map(({ type, collectionId }) => ({
      queryKey: collectionId
        ? getGetCollectionMembersQueryKey(collectionId)
        : ['student-claim-errors', 'members', type],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getMembers(collectionId ?? '', signal),
      enabled: !!collectionId,
    })),
  });

  const getClaims = useGetCollectionTicketClaimsHook();
  const claimQueries = useQueries({
    queries: collections.map(({ type, collectionId }) => ({
      queryKey: collectionId
        ? getGetCollectionTicketClaimsQueryKey(collectionId)
        : ['student-claim-errors', 'claims', type],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getClaims(collectionId ?? '', signal),
      enabled: !!collectionId,
    })),
  });

  const activeClaims = useMemo(
    () =>
      collections.flatMap((_, index) => {
        const claims = claimQueries[index]?.data ?? [];
        return claims
          .filter((claim) => getPeriodStatus(claim) !== 'past')
          .map((claim) => ({
            claimId: claim.id,
            collectionIndex: index,
          }));
      }),
    [collections, claimQueries],
  );

  const listMemberStates = useListCollectionTicketClaimMemberStatesHook();
  const stateQueries = useQueries({
    queries: activeClaims.map(({ claimId }) => ({
      queryKey: getListCollectionTicketClaimMemberStatesQueryKey(claimId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        listMemberStates(claimId, signal),
      enabled: !!claimId,
    })),
  });

  const errors = useMemo(() => {
    const statesByCollection: CollectionTicketClaimMemberStateDTO[][] =
      collections.map(() => []);

    activeClaims.forEach((claim, index) => {
      const data = stateQueries[index]?.data;
      if (!data) return;
      statesByCollection[claim.collectionIndex]?.push(...data);
    });

    return studentsWithClaimErrors(
      collections.flatMap((collection, index) => {
        const members = memberQueries[index]?.data;
        if (!members) return [];
        return [
          {
            collectionType: collection.type,
            members,
            states: statesByCollection[index] ?? [],
          },
        ];
      }),
    );
  }, [collections, memberQueries, activeClaims, stateQueries]);

  const isLoading =
    categories.length > 0 &&
    (isTreeLoading ||
      collectionQueries.some((query) => query.isLoading) ||
      memberQueries.some((query) => query.isLoading) ||
      claimQueries.some((query) => query.isLoading) ||
      stateQueries.some((query) => query.isLoading));

  return { errors, isLoading };
};
